// app/src/lib/revenue/channels.js — sales channel CRUD with default commission rates.
//
// commissionRateBps is stored in basis points (e.g. 3000 = 30%) so all platform-fee
// math stays in integer cents. Defaults match SPECIFICATION §8.1: DoorDash 30%,
// Uber Eats 30%, Grubhub 25%.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';

const DEFAULT_BPS = {
  'doordash': 3000,
  'ubereats': 3000,
  'grubhub':  2500,
  'dine-in':  0,
  'takeout':  0,
  'retail':   0,
  'catering': 0,
  'manual':   0,
};

/** @typedef {{ channelId:string, userId:string, name:string, type:string, commissionRateBps:number, isActive:0|1, plaidAccountId?:string }} Channel */

export async function create(input) {
  const userId = await requireUserId();
  if (!input.name) throw new Error('channel.create: name required');
  const type = (input.type || input.name).toLowerCase().replace(/[^a-z]/g, '');
  /** @type {Channel} */
  const ch = {
    channelId: input.channelId || crypto.randomUUID(),
    userId,
    name: input.name,
    type,
    commissionRateBps: clampBps(input.commissionRateBps ?? DEFAULT_BPS[type] ?? 0),
    isActive: 1,
    plaidAccountId: input.plaidAccountId,
  };
  await db.channels.put(ch);
  return ch;
}

export async function update(channelId, patch) {
  const userId = await requireUserId();
  const existing = await db.channels.get(channelId);
  if (!existing || existing.userId !== userId) throw new Error('channel.update: not found');
  const next = {
    ...existing,
    ...patch,
    commissionRateBps: patch.commissionRateBps != null ? clampBps(patch.commissionRateBps) : existing.commissionRateBps,
  };
  await db.channels.put(next);
  return next;
}

export async function deactivate(channelId) { return update(channelId, { isActive: 0 }); }

export async function listActive() {
  const userId = await requireUserId();
  return db.channels.where('[userId+isActive]').equals([userId, 1]).toArray();
}

/** Seed default channels for a brand-new user. Idempotent. */
export async function ensureDefaults() {
  const userId = await requireUserId();
  const existing = await db.channels.where('userId').equals(userId).count();
  if (existing > 0) return [];
  const seed = ['Dine-in', 'Takeout', 'DoorDash', 'Uber Eats', 'Grubhub', 'Retail', 'Catering', 'Manual'];
  const made = [];
  for (const name of seed) made.push(await create({ name }));
  return made;
}

function clampBps(n) {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(10000, v));
}
